import { Tracking } from '@msw/tracking';
import { PendoInstance } from './model/pendo-instance';
import { PendoIdentificationData } from './model/pendo-identification-data';
import { PendoInitializationData } from './model/pendo-initialization-data';

declare global {
  interface Window {
    pendo?: PendoInstance;
  }
}

interface PendoConfig {
  idlePatch?: boolean;
}

export class PendoTrackingProvider implements Tracking {
  private scriptReady?: Promise<void>;
  private config: PendoConfig = {
    idlePatch: true,
  };

  private get pendo(): PendoInstance | undefined {
    return window.pendo;
  }

  configure(config: PendoConfig = {}): void {
    Object.entries(config).forEach(([key, value]) => {
      if (value !== undefined) this.config[key as keyof PendoConfig] = value;
    });
  }

  identify(identity: PendoIdentificationData): void {
    if (!this.pendo?.initialize || !identity.accountId || !identity.visitorId) {
      return;
    }

    this.pendo.initialize({
      visitor: {
        id: identity.visitorId,
      },
      account: {
        id: identity.accountId,
      },
      events: this.config.idlePatch
        ? { ready: () => this.patchEventDispatcher() }
        : undefined,
    });
  }

  async initialize(identity?: PendoInitializationData): Promise<void> {
    if (this.scriptReady) {
      return this.scriptReady;
    }

    this.scriptReady = this.importScript(identity)
      .then(() => {
        if (identity) {
          this.identify(identity as PendoIdentificationData);
        }
      })
      .catch((e) => console.warn(e.message));

    return this.scriptReady;
  }

  private importScript(identity?: PendoInitializationData) {
    return new Promise<void>((resolve, reject) => {
      if (this.pendo || document.getElementById('pendo-agent')) {
        if (identity) {
          this.identify(identity as PendoIdentificationData);
        }
        return resolve();
      }

      if (!identity?.apiKey) {
        return reject(
          new Error('[Pendo] API key is required for initialization')
        );
      }

      const script = document.createElement('script');
      script.id = 'pendo-agent';
      script.async = true;
      script.src = `https://cdn.eu.pendo.io/agent/static/${identity.apiKey}/pendo.js`;
      script.onload = () => {
        resolve();
      };
      script.onerror = () =>
        reject(
          new Error(
            `[Pendo] script "https://cdn.eu.pendo.io/agent/static/${identity.apiKey}/pendo.js" failed to load`
          )
        );

      document.head.appendChild(script);
    });
  }

  private patchEventDispatcher(): void {
    if (!this.config.idlePatch) return;

    const pendo = this.pendo;
    if (!pendo?.dom?.event) {
      return;
    }
    const pendoEvent = pendo.dom.event;
    if (pendoEvent['__idlePatched']) {
      return;
    }

    const originalDispatch = pendoEvent.dispatch;
    const scheduler =
      window.requestIdleCallback ?? window.requestAnimationFrame;

    pendo.dom.event.dispatch = (element: any, ev: any) => {
      if (['click', 'focus', 'mouseover'].includes(ev.type)) {
        const clone = pendoEvent.clone(ev);
        scheduler(() => originalDispatch.call(pendoEvent, element, clone));
      } else {
        originalDispatch.call(pendoEvent, element, ev);
      }
    };

    pendoEvent['__idlePatched'] = true;
  }
}
